/**
 * Create Incident Page
 * Page for creating new incidents
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IncidentForm from '../components/incidents/IncidentForm';
import type { CreateIncidentRequest, UpdateIncidentRequest } from '../types/incident';
import { createIncident } from '../services/incidentService';
import { getHotels } from '../services/hotelService';

const CreateIncidentPage: React.FC = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHotels();
  }, []);

  const loadHotels = async () => {
    try {
      const response = await getHotels();
      setHotels(
        response.map((hotel: any) => ({
          id: hotel.id,
          name: hotel.name,
        }))
      );
    } catch (err) {
      console.error('Error loading hotels:', err);
      setError('Erreur lors du chargement des hôtels');
    }
  };

  const handleSubmit = async (data: CreateIncidentRequest | UpdateIncidentRequest) => {
    try {
      setLoading(true);
      setError(null);
      const incident = await createIncident(data as CreateIncidentRequest);
      navigate(`/incidents/${incident.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erreur lors de la création de l\'incident');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/incidents');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/incidents')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Retour aux incidents
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Nouvel Incident</h1>
          <p className="mt-1 text-sm text-gray-600">
            Créer un nouveau rapport d'incident
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="ml-3 text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <IncidentForm
            hotels={hotels}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateIncidentPage;
